import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import RemoveIcon from '@mui/icons-material/Remove';
import CodeIcon from '@mui/icons-material/Code';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SmartButtonIcon from '@mui/icons-material/SmartButton';
import {
  Box,
  Paper,
  IconButton,
  ButtonBase,
  Grid,
  Typography,
  Tooltip,
} from '@mui/material';
import ButtonBlock from './ButtonBlock';
import HeaderBlock from './HeaderBlock';
import ImageBlock from './ImageBlock';
import TextBlock from './TextBlock';
import SpacerBlock from './SpacerBlock';
import DividerBlock from './DividerBlock';
import HtmlCodeBlock from './HtmlCodeBlock';
import ContainerBlock from './ContainerBlock';
import ColumnContainerBlock from './ColumnContainerBlock';
import ColumnBlock from './ColumnBlock';
import { BLOCK_TYPES } from '../../utilities/blockTypes'
import useEmailEditorStore from '../../utilities/emailEditorStore';

function EmailEditorBlock({
  blockId,
  blockType,
  blockProperties,
  blockParentId,
  blockChildren,
  isRoot = false,
}) {
  const [showTopBlockMenu, setShowTopBlockMenu] = useState(false);
  const [showBottomBlockMenu, setShowBottomBlockMenu] = useState(false);

  // Get store functions and state
  const {
    selectedBlockId,
    selectBlock,
    addBlock,
    removeBlock,
    moveBlock,
    getParentBlock,
    getAllBlocks,
    getBlockById,
    clearSelection,
  } = useEmailEditorStore();

  const isSelected = selectedBlockId === blockId;

  const blockTypes = [
    { name: 'Header', type: BLOCK_TYPES.HEADER, icon: TextFieldsIcon, description: 'Add a heading block' },
    { name: 'Text', type: BLOCK_TYPES.TEXT, icon: DescriptionIcon, description: 'Add a text block' },
    { name: 'Image', type: BLOCK_TYPES.IMAGE, icon: ImageIcon, description: 'Add an image block' },
    { name: 'Button', type: BLOCK_TYPES.BUTTON, icon: SmartButtonIcon, description: 'Add a button block' },
    { name: 'Container', type: BLOCK_TYPES.CONTAINER, icon: CheckBoxOutlineBlankIcon, description: 'Add a container block' },
    { name: 'Column',type:BLOCK_TYPES.COLUMNCONTAINER,icon: ViewColumnIcon, description:'Add column'},
    { name: 'Spacer', type: BLOCK_TYPES.SPACER, icon: DragHandleIcon, description: 'Add spacing between blocks' },
    { name: 'Divider', type: BLOCK_TYPES.DIVIDER, icon: RemoveIcon, description: 'Add a divider line' },
    { name: 'Html', type: BLOCK_TYPES.HTMLCONTENT, icon: CodeIcon, description: 'Add custom HTML code' },
  ];

  // Helper function to get sibling blocks and current index
  const getSiblingInfo = () => {
    const parent = getParentBlock(blockId);
    const siblings = parent ? parent.children : getAllBlocks().filter(block => !block.parentId);
    const currentIndex = siblings.findIndex(block => block.id === blockId);
    return { siblings, currentIndex };
  };

  const { siblings, currentIndex } = getSiblingInfo();
  const canMoveUp = currentIndex > 0;
  const canMoveDown = currentIndex < siblings.length - 1;

  const handleBlockClick = (e) => {
    e.stopPropagation();
  
      // Check if this block is already selected
      if (selectedBlockId === blockId) {
        // If already selected, unselect it
        clearSelection();
      } else {
        // If not selected, select it
        selectBlock(blockId);
      }
  
  // Hide menus regardless of selection state
  setShowTopBlockMenu(false);
  setShowBottomBlockMenu(false);
  };

  const handleTopPlusClick = (e) => {
    e.stopPropagation();
    setShowTopBlockMenu(!showTopBlockMenu);
    setShowBottomBlockMenu(false);
  };

  const handleBottomPlusClick = (e) => {
    e.stopPropagation();
    setShowBottomBlockMenu(!showBottomBlockMenu);
    setShowTopBlockMenu(false);
  };

  const handleBlockTypeSelect = (blockTypeInfo, position) => {
    const parent = getParentBlock(blockId);
    const parentId = parent ? parent.id : null;
    const { siblings, currentIndex } = getSiblingInfo();
    
    let insertIndex;
    if (position === 'top') {
      insertIndex = currentIndex;
    } else {
      insertIndex = currentIndex + 1;
    }

    addBlock(blockTypeInfo.type, parentId, insertIndex);
    setShowTopBlockMenu(false);
    setShowBottomBlockMenu(false);
  };

  const handleMoveUp = (e) => {
    e.stopPropagation();
    if (canMoveUp) {
      const parent = getParentBlock(blockId);
      const parentId = parent ? parent.id : null;
      const newIndex = currentIndex - 1;
      
      moveBlock(blockId, parentId, newIndex);
    }
    setShowTopBlockMenu(false);
    setShowBottomBlockMenu(false);
  };

  const handleMoveDown = (e) => {
    e.stopPropagation();
    if (canMoveDown) {
      const parent = getParentBlock(blockId);
      const parentId = parent ? parent.id : null;
      const newIndex = currentIndex + 1;
      
      moveBlock(blockId, parentId, newIndex);
    }
    setShowTopBlockMenu(false);
    setShowBottomBlockMenu(false);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    removeBlock(blockId);
    setShowTopBlockMenu(false);
    setShowBottomBlockMenu(false);
  };

  const renderBlockContent = () => {
    const commonProps = {
      id: blockId,
      type: blockType,
      properties: blockProperties,
    };

    switch (blockType) {
      case BLOCK_TYPES.HEADER:
        return <HeaderBlock {...commonProps} />;
      case BLOCK_TYPES.IMAGE:
        return <ImageBlock {...commonProps} />;
      case BLOCK_TYPES.TEXT:
        return <TextBlock {...commonProps} />;
      case BLOCK_TYPES.BUTTON:
        return <ButtonBlock {...commonProps} />;
      case BLOCK_TYPES.SPACER:
        return <SpacerBlock {...commonProps} />;
      case BLOCK_TYPES.DIVIDER:
        return <DividerBlock {...commonProps} />;
      case BLOCK_TYPES.HTMLCONTENT:
        return <HtmlCodeBlock {...commonProps} />;
      case BLOCK_TYPES.CONTAINER:
        return <ContainerBlock {...commonProps} children={blockChildren} />;
      case BLOCK_TYPES.COLUMNCONTAINER:
        return <ColumnContainerBlock {...commonProps} children={blockChildren} />
      case BLOCK_TYPES.COLUMN:
        return <ColumnBlock {...commonProps} children={blockChildren} />;
      default:
        return <Typography variant="body2">Unknown Block Type: {blockType}</Typography>;
    }
  };

  const renderBlockMenu = (position) => (
    <Paper
      sx={{
        position: 'absolute',
        top: 40,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 280,
        py: 1,
        zIndex: 30,
        boxShadow: 3,
      }}
      variant="outlined"
    >
      <Grid container spacing={1}>
        {blockTypes.map((blockType) => {
          const IconComponent = blockType.icon;
          return (
            <Grid item xs={6} key={blockType.name}>
              <Tooltip title={blockType.description} placement="top">
                <ButtonBase
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBlockTypeSelect(blockType, position);
                  }}
                  sx={{
                    width: '100%',
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    textAlign: 'left',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  <IconComponent fontSize="small" color="action" />
                  <Typography variant="caption" noWrap>
                    {blockType.name}
                  </Typography>
                </ButtonBase>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );

  return (
    <Box 
      position="relative" 
      sx={{ 
        m: isSelected ? 0 : 0, 
         p: isSelected ? 0 : 0, 
        overflow: 'visible',
        transition: 'margin 0.2s ease-in-out',
      }}
    >
      {/* Top Plus Icon and Menu */}
      {isSelected && (
        <Box 
          position="absolute" 
          top={-28} 
          left="50%" 
          sx={{ transform: 'translateX(-50%)' }} 
          zIndex={21}
        >
          <Paper elevation={2} sx={{ borderRadius: '50%' }}>
            <IconButton
              onClick={handleTopPlusClick}
              size="small"
              sx={{
                color: showTopBlockMenu ? 'common.white' : 'primary.main',
                bgcolor: showTopBlockMenu ? 'primary.main' : 'grey.200',
                '&:hover': {
                  bgcolor: showTopBlockMenu ? 'primary.dark' : 'grey.300',
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Paper>

          {showTopBlockMenu && renderBlockMenu('top')}
        </Box>
      )}

      {/* Left Side Controls */}
      {isSelected && (
        <Box
          position="absolute"
          left={-28}
          top="50%"
          sx={{ transform: 'translateY(-50%)', backgroundColor:'#ececec',borderRadius:'20%' }}
          zIndex={10}
          display="flex"
          flexDirection="column"
          gap={1}
          
        >
          {canMoveUp && (
            <Tooltip title="Move up">
              <IconButton
                onClick={handleMoveUp}
                size="small"
                sx={{
                  bgcolor: 'grey.200',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'grey.300',
                  },
                }}
              >
                <ExpandLessIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canMoveDown && (
            <Tooltip title="Move down">
              <IconButton
                onClick={handleMoveDown}
                size="small"
                sx={{
                  bgcolor: 'grey.200',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'grey.300',
                  },
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete block">
            <IconButton
              onClick={handleDelete}
              size="small"
              sx={{
                bgcolor: 'grey.200',
                color: 'primary.main',
                '&:hover': {
                  color: 'error.main',
                  bgcolor: 'error.light',
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Main Content Block */}
      <Paper
        onClick={handleBlockClick}
        sx={{
          border: isSelected ? '2px solid' : '0px solid transparent',
          borderColor: isSelected ? 'primary.main' : 'transparent',
          boxShadow: isSelected ? 3 : 0,
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          bgcolor: isSelected ? 'white' : 'transparent',
          '&:hover': {
            boxShadow: !isSelected ? 1 : undefined,
          },
        }}
      >
        {renderBlockContent()}
      </Paper>

      {/* Bottom Plus Icon and Menu */}
      {isSelected && (
        <Box 
          position="absolute" 
          bottom={-24} 
          left="50%" 
          sx={{ transform: 'translateX(-50%)' }} 
          zIndex={20}
        >
          <Paper elevation={2} sx={{ borderRadius: '50%' }}>
            <IconButton
              onClick={handleBottomPlusClick}
              size="small"
              sx={{
                color: showBottomBlockMenu ? 'common.white' : 'primary.main',
                bgcolor: showBottomBlockMenu ? 'primary.main' : 'grey.200',
                '&:hover': {
                  bgcolor: showBottomBlockMenu ? 'primary.dark' : 'grey.300',
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Paper>

          {showBottomBlockMenu && renderBlockMenu('bottom')}
        </Box>
      )}
    </Box>
  );
}

export default EmailEditorBlock;